import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  LocationOn as LocationIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { db } from '../../../firebase/config';
import { collection, query, orderBy, getDocs, Timestamp, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';

type JobSchedule = {
  type: 'daily' | 'weekly' | 'monthly' | 'custom';
  time?: string; // HH:MM format
  days?: number[]; // 0 = Sunday, 1 = Monday, etc.
  date?: number; // Day of month
};

type JobStatus = 'scheduled' | 'running' | 'completed' | 'failed' | 'cancelled';

type ScheduledJob = {
  id: string;
  name: string;
  description?: string;
  sourceId: string;
  sourceName: string;
  schedule: JobSchedule;
  status: JobStatus;
  lastRun?: Timestamp;
  nextRun?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  parameters?: {
    lookbackDays?: number;
    categories?: string[];
    locations?: string[];
    keywords?: string[];
    includeOnline?: boolean;
    limit?: number;
    startDate?: Timestamp | null;
    endDate?: Timestamp | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    radius?: number | null; // in miles or km
  };
  logs?: Array<{
    timestamp: Timestamp;
    message: string;
    level: 'info' | 'warning' | 'error';
  }>;
};

const ScheduledJobs: React.FC = () => {
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openJobDialog, setOpenJobDialog] = useState(false);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Partial<ScheduledJob>>({
    name: '',
    description: '',
    sourceId: '',
    schedule: {
      type: 'daily',
      time: '00:00'
    },
    parameters: {
      lookbackDays: 30,
      includeOnline: true,
      limit: 100,
      startDate: null,
      endDate: null,
      city: null,
      state: null,
      country: null,
      radius: 25
    }
  });
  const [sources, setSources] = useState<Array<{ id: string; name: string }>>([]);
  const [openLogsDialog, setOpenLogsDialog] = useState(false);
  const [selectedJobLogs, setSelectedJobLogs] = useState<ScheduledJob | null>(null);
  const [processingJobId, setProcessingJobId] = useState<string | null>(null);

  useEffect(() => {
    fetchJobs();
    fetchSources();
  }, []);

  const fetchSources = async () => {
    // In a real implementation, fetch from Firestore
    // For now, using mock data
    setSources([
      { id: '1', name: 'Eventbrite' },
      { id: '2', name: 'Meetup' },
      { id: '3', name: 'Facebook Events' }
    ]);
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // In a real implementation, fetch from Firestore
      // For now, using mock data
      const mockJobs: ScheduledJob[] = [
        {
          id: 'job1',
          name: 'Eventbrite Daily Sync',
          description: 'Sync new events from Eventbrite daily',
          sourceId: '1',
          sourceName: 'Eventbrite',
          schedule: {
            type: 'daily',
            time: '03:00'
          },
          status: 'scheduled',
          lastRun: Timestamp.fromDate(new Date(Date.now() - 12 * 3600 * 1000)),
          nextRun: Timestamp.fromDate(new Date(Date.now() + 12 * 3600 * 1000)),
          createdAt: Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 3600 * 1000)),
          updatedAt: Timestamp.fromDate(new Date(Date.now() - 2 * 24 * 3600 * 1000)),
          parameters: {
            lookbackDays: 7,
            categories: ['music', 'food-and-drink', 'charity-and-causes'],
            includeOnline: true,
            limit: 1000,
            startDate: Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 3600 * 1000)),
            endDate: Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 3600 * 1000)),
            city: 'New York',
            state: 'NY',
            country: 'USA',
            radius: 50
          },
          logs: [
            {
              timestamp: Timestamp.fromDate(new Date(Date.now() - 12 * 3600 * 1000)),
              message: 'Job completed successfully. 157 events imported.',
              level: 'info'
            },
            {
              timestamp: Timestamp.fromDate(new Date(Date.now() - 36 * 3600 * 1000)),
              message: 'Job completed successfully. 142 events imported.',
              level: 'info'
            }
          ]
        },
        {
          id: 'job2',
          name: 'Meetup Weekly Full Sync',
          description: 'Complete sync of all events from Meetup once a week',
          sourceId: '2',
          sourceName: 'Meetup',
          schedule: {
            type: 'weekly',
            days: [0], // Sunday
            time: '04:00'
          },
          status: 'completed',
          lastRun: Timestamp.fromDate(new Date(Date.now() - 1 * 24 * 3600 * 1000)),
          nextRun: Timestamp.fromDate(new Date(Date.now() + 6 * 24 * 3600 * 1000)),
          createdAt: Timestamp.fromDate(new Date(Date.now() - 60 * 24 * 3600 * 1000)),
          updatedAt: Timestamp.fromDate(new Date(Date.now() - 5 * 24 * 3600 * 1000)),
          parameters: {
            lookbackDays: 30,
            categories: ['tech', 'career', 'outdoors'],
            includeOnline: true,
            startDate: Timestamp.fromDate(new Date(Date.now() - 60 * 24 * 3600 * 1000)),
            endDate: Timestamp.fromDate(new Date(Date.now() + 60 * 24 * 3600 * 1000)),
            city: 'San Francisco',
            state: 'CA',
            country: 'USA',
            radius: 75
          },
          logs: [
            {
              timestamp: Timestamp.fromDate(new Date(Date.now() - 1 * 24 * 3600 * 1000)),
              message: 'Job completed successfully. 328 events imported.',
              level: 'info'
            }
          ]
        },
        {
          id: 'job3',
          name: 'Facebook Events Error',
          description: 'Sample job with error',
          sourceId: '3',
          sourceName: 'Facebook Events',
          schedule: {
            type: 'daily',
            time: '05:00'
          },
          status: 'failed',
          lastRun: Timestamp.fromDate(new Date(Date.now() - 4 * 3600 * 1000)),
          nextRun: Timestamp.fromDate(new Date(Date.now() + 20 * 3600 * 1000)),
          createdAt: Timestamp.fromDate(new Date(Date.now() - 20 * 24 * 3600 * 1000)),
          updatedAt: Timestamp.fromDate(new Date(Date.now() - 1 * 24 * 3600 * 1000)),
          logs: [
            {
              timestamp: Timestamp.fromDate(new Date(Date.now() - 4 * 3600 * 1000)),
              message: 'Error: API rate limit exceeded',
              level: 'error'
            },
            {
              timestamp: Timestamp.fromDate(new Date(Date.now() - 28 * 3600 * 1000)),
              message: 'Job completed successfully. 76 events imported.',
              level: 'info'
            }
          ]
        }
      ];
      
      setJobs(mockJobs);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again.');
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenJobDialog = (jobId?: string) => {
    if (jobId) {
      const job = jobs.find(j => j.id === jobId);
      if (job) {
        setFormValues({
          name: job.name,
          description: job.description,
          sourceId: job.sourceId,
          schedule: { ...job.schedule },
          parameters: { ...job.parameters }
        });
        setEditingJobId(jobId);
      }
    } else {
      setFormValues({
        name: '',
        description: '',
        sourceId: '',
        schedule: {
          type: 'daily',
          time: '00:00'
        },
        parameters: {
          lookbackDays: 30,
          includeOnline: true,
          limit: 100,
          startDate: null,
          endDate: null,
          city: null,
          state: null,
          country: null,
          radius: 25
        }
      });
      setEditingJobId(null);
    }
    setOpenJobDialog(true);
  };

  const handleCloseJobDialog = () => {
    setOpenJobDialog(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleScheduleChange = (name: string, value: any) => {
    setFormValues(prev => {
      // Make sure prev.schedule exists with the required properties
      const currentSchedule = prev.schedule || { type: 'daily', time: '00:00' };
      
      return {
        ...prev,
        schedule: {
          ...currentSchedule,
          [name]: value
        }
      };
    });
  };

  const handleParameterChange = (name: string, value: any) => {
    setFormValues(prev => {
      // Make sure prev.parameters exists with required properties
      const currentParameters = prev.parameters || {
        lookbackDays: 30,
        includeOnline: true,
        limit: 100,
        startDate: null,
        endDate: null,
        city: null,
        state: null,
        country: null,
        radius: 25
      };
      
      return {
        ...prev,
        parameters: {
          ...currentParameters,
          [name]: value
        }
      };
    });
  };

  const handleSubmitJob = () => {
    // Validation would go here
    
    if (editingJobId) {
      // Update existing job
      const updatedJob: ScheduledJob = {
        ...jobs.find(j => j.id === editingJobId)!,
        ...formValues,
        updatedAt: Timestamp.fromDate(new Date())
      } as ScheduledJob;
      
      // In a real implementation, you would update Firestore
      // Update local state
      setJobs(prev => 
        prev.map(job => job.id === editingJobId ? updatedJob : job)
      );
    } else {
      // Create new job
      const sourceName = sources.find(s => s.id === formValues.sourceId)?.name || '';
      const newJob: ScheduledJob = {
        ...formValues,
        id: Math.random().toString(36).substring(2, 9), // Temporary ID
        sourceName,
        status: 'scheduled',
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
        nextRun: Timestamp.fromDate(new Date(Date.now() + 24 * 3600 * 1000)) // Just an example
      } as ScheduledJob;
      
      // In a real implementation, you would add to Firestore
      // Update local state
      setJobs(prev => [...prev, newJob]);
    }
    
    handleCloseJobDialog();
  };

  const handleDeleteJob = (jobId: string) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      // In a real implementation, you would delete from Firestore
      // Update local state
      setJobs(prev => prev.filter(job => job.id !== jobId));
    }
  };

  const handleRunNow = async (jobId: string) => {
    try {
      setProcessingJobId(jobId);
      
      // Simulate job execution
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update job status
      setJobs(prev => 
        prev.map(job => {
          if (job.id === jobId) {
            const now = new Date();
            return {
              ...job,
              status: 'completed',
              lastRun: Timestamp.fromDate(now),
              logs: [
                {
                  timestamp: Timestamp.fromDate(now),
                  message: 'Manual execution completed successfully. 42 events imported.',
                  level: 'info'
                },
                ...(job.logs || [])
              ]
            };
          }
          return job;
        })
      );
      
      alert('Job executed successfully!');
    } catch (err) {
      console.error('Error running job:', err);
      setError('Failed to execute job. Please try again.');
    } finally {
      setProcessingJobId(null);
    }
  };

  const handleViewLogs = (job: ScheduledJob) => {
    setSelectedJobLogs(job);
    setOpenLogsDialog(true);
  };

  const formatDateTime = (timestamp: Timestamp | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleString();
  };

  const formatSchedule = (schedule: JobSchedule) => {
    switch (schedule.type) {
      case 'daily':
        return `Daily at ${schedule.time || '00:00'}`;
      case 'weekly':
        const days = schedule.days || [];
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return `Weekly on ${days.map(d => dayNames[d]).join(', ')} at ${schedule.time || '00:00'}`;
      case 'monthly':
        return `Monthly on day ${schedule.date || 1} at ${schedule.time || '00:00'}`;
      default:
        return 'Custom schedule';
    }
  };

  const formatParameters = (job: ScheduledJob) => {
    const params = job.parameters;
    if (!params) return 'Default parameters';
    
    let details = [];
    
    // Location details
    if (params.city || params.state || params.country) {
      let location = [];
      if (params.city) location.push(params.city);
      if (params.state) location.push(params.state);
      if (params.country) location.push(params.country);
      details.push(`Location: ${location.join(', ')}`);
      
      if (params.radius) {
        details.push(`Radius: ${params.radius} miles`);
      }
    }
    
    // Date range details
    if (params.startDate || params.endDate) {
      let dateRange = [];
      if (params.startDate) dateRange.push(`From: ${formatDateTime(params.startDate)}`);
      if (params.endDate) dateRange.push(`To: ${formatDateTime(params.endDate)}`);
      details.push(dateRange.join(' '));
    }
    
    // Other parameters
    if (params.lookbackDays) details.push(`Lookback: ${params.lookbackDays} days`);
    if (params.limit) details.push(`Limit: ${params.limit} events`);
    if (params.categories && params.categories.length > 0) {
      details.push(`Categories: ${params.categories.join(', ')}`);
    }
    
    return details.join(' | ');
  };

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case 'scheduled':
        return 'primary';
      case 'running':
        return 'warning';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Scheduled Jobs
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenJobDialog()}
        >
          Add Job
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Jobs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Parameters</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last/Next Run</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No scheduled jobs found
                  </TableCell>
                </TableRow>
              ) : (
                jobs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {job.name}
                        </Typography>
                        {job.description && (
                          <Typography variant="caption" color="text.secondary">
                            {job.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{job.sourceName}</TableCell>
                      <TableCell>{formatSchedule(job.schedule)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ display: 'block', maxWidth: 250, whiteSpace: 'normal' }}>
                          {formatParameters(job)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={job.status} 
                          color={getStatusColor(job.status)} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          Last: {formatDateTime(job.lastRun)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Next: {formatDateTime(job.nextRun)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Run Now">
                          <IconButton 
                            color="primary" 
                            onClick={() => handleRunNow(job.id)}
                            disabled={processingJobId === job.id}
                          >
                            {processingJobId === job.id ? (
                              <CircularProgress size={24} />
                            ) : (
                              <PlayArrowIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Logs">
                          <IconButton 
                            color="info" 
                            onClick={() => handleViewLogs(job)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Job">
                          <IconButton 
                            onClick={() => handleOpenJobDialog(job.id)}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Job">
                          <IconButton 
                            color="error" 
                            onClick={() => handleDeleteJob(job.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={jobs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Job Form Dialog */}
      <Dialog 
        open={openJobDialog} 
        onClose={handleCloseJobDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingJobId ? 'Edit Scheduled Job' : 'Add New Scheduled Job'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Job Name"
                name="name"
                value={formValues.name || ''}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Data Source</InputLabel>
                <Select
                  name="sourceId"
                  value={formValues.sourceId || ''}
                  onChange={handleSelectChange}
                  label="Data Source"
                  required
                >
                  {sources.map(source => (
                    <MenuItem key={source.id} value={source.id}>
                      {source.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formValues.description || ''}
                onChange={handleInputChange}
                multiline
                rows={2}
                margin="normal"
              />
            </Grid>
            
            {/* Schedule Settings */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon sx={{ mr: 1 }} color="primary" /> Schedule
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Schedule Type</InputLabel>
                <Select
                  value={formValues.schedule?.type || 'daily'}
                  onChange={(e) => handleScheduleChange('type', e.target.value)}
                  label="Schedule Type"
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Time (HH:MM)"
                value={formValues.schedule?.time || '00:00'}
                onChange={(e) => handleScheduleChange('time', e.target.value)}
                margin="normal"
                type="time"
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />
            </Grid>
            
            {formValues.schedule?.type === 'weekly' && (
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Days of Week</InputLabel>
                  <Select
                    multiple
                    value={formValues.schedule?.days || [0]}
                    onChange={(e) => handleScheduleChange('days', e.target.value as number[])}
                    label="Days of Week"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((value) => {
                          const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                          return <Chip key={value} label={days[value]} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    <MenuItem value={0}>Sunday</MenuItem>
                    <MenuItem value={1}>Monday</MenuItem>
                    <MenuItem value={2}>Tuesday</MenuItem>
                    <MenuItem value={3}>Wednesday</MenuItem>
                    <MenuItem value={4}>Thursday</MenuItem>
                    <MenuItem value={5}>Friday</MenuItem>
                    <MenuItem value={6}>Saturday</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {formValues.schedule?.type === 'monthly' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Day of Month"
                  type="number"
                  value={formValues.schedule?.date || 1}
                  onChange={(e) => handleScheduleChange('date', parseInt(e.target.value, 10))}
                  margin="normal"
                  InputProps={{ inputProps: { min: 1, max: 31 } }}
                />
              </Grid>
            )}
            
            {/* Parameters */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
                <SettingsIcon sx={{ mr: 1 }} color="primary" /> Basic Parameters
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Lookback Days"
                type="number"
                value={formValues.parameters?.lookbackDays || 30}
                onChange={(e) => handleParameterChange('lookbackDays', parseInt(e.target.value, 10))}
                margin="normal"
                helperText="Number of days to look back for events"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maximum Events"
                type="number"
                value={formValues.parameters?.limit || 100}
                onChange={(e) => handleParameterChange('limit', parseInt(e.target.value, 10))}
                margin="normal"
                helperText="Limit number of events to import"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.parameters?.includeOnline || false}
                    onChange={(e) => handleParameterChange('includeOnline', e.target.checked)}
                  />
                }
                label="Include Online Events"
              />
            </Grid>
            
            {/* Location Parameters */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
                <LocationIcon sx={{ mr: 1 }} color="primary" /> Location Filters
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={formValues.parameters?.city || ''}
                onChange={(e) => handleParameterChange('city', e.target.value)}
                margin="normal"
                placeholder="e.g. New York"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="State/Province"
                value={formValues.parameters?.state || ''}
                onChange={(e) => handleParameterChange('state', e.target.value)}
                margin="normal"
                placeholder="e.g. NY"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={formValues.parameters?.country || ''}
                onChange={(e) => handleParameterChange('country', e.target.value)}
                margin="normal"
                placeholder="e.g. USA"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Radius (miles)"
                type="number"
                value={formValues.parameters?.radius || 25}
                onChange={(e) => handleParameterChange('radius', parseInt(e.target.value, 10))}
                margin="normal"
                helperText="Search radius around specified location"
                InputProps={{ inputProps: { min: 0, max: 500 } }}
              />
            </Grid>
            
            {/* Date Range Parameters */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
                <EventIcon sx={{ mr: 1 }} color="primary" /> Date Range Filters
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formValues.parameters?.startDate ? new Date(formValues.parameters.startDate.seconds * 1000).toISOString().split('T')[0] : ''}
                onChange={(e) => handleParameterChange('startDate', e.target.value ? Timestamp.fromDate(new Date(e.target.value)) : null)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                helperText="Events from this date (inclusive)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formValues.parameters?.endDate ? new Date(formValues.parameters.endDate.seconds * 1000).toISOString().split('T')[0] : ''}
                onChange={(e) => handleParameterChange('endDate', e.target.value ? Timestamp.fromDate(new Date(e.target.value)) : null)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                helperText="Events up to this date (inclusive)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseJobDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitJob} 
            variant="contained" 
            color="primary"
          >
            {editingJobId ? 'Update' : 'Create'} Job
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Logs Dialog */}
      <Dialog 
        open={openLogsDialog} 
        onClose={() => setOpenLogsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Job History - {selectedJobLogs?.name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedJobLogs?.logs && selectedJobLogs.logs.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {selectedJobLogs.logs.map((log, index) => (
                <Alert 
                  key={index} 
                  severity={log.level === 'error' ? 'error' : log.level === 'warning' ? 'warning' : 'info'}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    {formatDateTime(log.timestamp)}
                  </Typography>
                  {log.message}
                </Alert>
              ))}
            </Box>
          ) : (
            <Typography>No logs available for this job.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledJobs;
